/**
 * Coach LLM Narrator — orchestration layer.
 *
 * Sits between the React hook (useLlmNarrator) and the bare API client
 * (anthropicClient). Responsibilities:
 *
 *   1. Build the prompt from the scope + observations (delegates to promptBuilder).
 *   2. Check the in-memory cache. Hit → return immediately, no API call.
 *   3. On miss → call the Anthropic Messages API.
 *   4. On success → cache the response, return narrative + token usage.
 *   5. On failure → return structured error so caller can fall back to
 *      the templated synthesis paragraph.
 *
 * Cache:
 *   - In-memory Map keyed by deterministic observation-set hash.
 *   - LRU-trimmed at CACHE_MAX entries so a long session doesn't grow unbounded.
 *   - NOT persisted across app restarts. The synthesis card in the
 *     dashboard fires on-demand (button click), not on every render, so
 *     in-memory is enough; a future enhancement could persist via IndexedDB.
 *
 * Why not persist the cache:
 *   - Observations change as the trader takes more trades. Yesterday's
 *     LLM paragraph is stale even if the cache key happened to match.
 *   - The cost is small enough ($0.005-$0.01 per call) that re-burning on
 *     a fresh launch is fine.
 *
 * Cost tracking:
 *   - Every successful call reports inputTokens, outputTokens, costUSD.
 *   - The hook accumulates these across calls and surfaces in the
 *     Settings UI so the user sees their spend.
 */

import { callAnthropic, estimateCostUSD, DEFAULT_MODEL, DEFAULT_MAX_TOKENS } from './anthropicClient.js';
import { buildSystemPrompt, buildUserPrompt, buildCacheKey } from './promptBuilder.js';

const CACHE_MAX = 64;
const _cache = new Map(); // cacheKey → { text, inputTokens, outputTokens, model, costUSD, generatedAt }

/**
 * LRU bookkeeping — re-set on access bumps the key to most-recent insertion
 * order; trim from the front (oldest insertion) when over CACHE_MAX.
 */
const cacheGet = (key) => {
  if (!_cache.has(key)) return null;
  const v = _cache.get(key);
  _cache.delete(key);
  _cache.set(key, v);
  return v;
};
const cachePut = (key, val) => {
  if (_cache.has(key)) _cache.delete(key);
  _cache.set(key, val);
  while (_cache.size > CACHE_MAX) {
    const first = _cache.keys().next().value;
    _cache.delete(first);
  }
};

/**
 * Public API: narrate one set of observations as a coaching paragraph.
 *
 * @param {Object} args
 * @param {Array}  args.observations
 * @param {string} args.scope         'setup' | 'atm' | 'day' | 'session'
 * @param {string} args.scopeId
 * @param {number} [args.tradeCount]
 * @param {string} args.apiKey
 * @param {string} [args.model]
 * @param {number} [args.maxTokens]
 * @param {AbortSignal} [args.signal]
 * @param {boolean} [args.skipCache]  Bypass cache (force re-call); useful for "Refresh"
 * @returns {Promise<{ok: true, text: string, fromCache: boolean, model: string, inputTokens: number, outputTokens: number, costUSD: number, generatedAt: string} | {ok: false, error: string, status?: number}>}
 */
export const narrate = async ({
  observations = [],
  scope = 'global',
  scopeId = '',
  tradeCount = null,
  apiKey,
  model = DEFAULT_MODEL,
  maxTokens = DEFAULT_MAX_TOKENS,
  voice = '',           // 'mentor' | 'analyst' | 'drill' | 'friend' | '' (default)
  signal,
  skipCache = false,
}) => {
  if (!apiKey) {
    return { ok: false, error: 'No API key configured' };
  }
  if (!Array.isArray(observations) || observations.length === 0) {
    return { ok: false, error: 'No observations to narrate' };
  }

  const key = buildCacheKey({ scope, scopeId, observations, voice });

  if (!skipCache) {
    const hit = cacheGet(key);
    if (hit) {
      return { ...hit, ok: true, fromCache: true };
    }
  }

  const system = buildSystemPrompt(voice);
  const user = buildUserPrompt({ observations, scope, scopeId, tradeCount });

  const result = await callAnthropic({ apiKey, system, user, model, maxTokens, signal });
  if (!result.ok) return result;

  // Cost estimator now factors prompt-cache write/read pricing — pass the
  // four token classes (non-cached input, output, cache write, cache read).
  const costUSD = estimateCostUSD(
    result.model,
    result.inputTokens,
    result.outputTokens,
    result.cacheCreationTokens,
    result.cacheReadTokens,
  );
  const cached = {
    text: result.text,
    model: result.model,
    inputTokens: result.inputTokens,
    outputTokens: result.outputTokens,
    cacheCreationTokens: result.cacheCreationTokens || 0,
    cacheReadTokens: result.cacheReadTokens || 0,
    costUSD,
    generatedAt: new Date().toISOString(),
  };
  cachePut(key, cached);
  return { ...cached, ok: true, fromCache: false };
};

/**
 * Manually drop the in-memory cache. Used by the Settings "clear cache"
 * affordance + by test setup/teardown.
 */
export const clearNarrationCache = () => {
  _cache.clear();
};

/**
 * Inspect cache size (for telemetry / diagnostics).
 */
export const narrationCacheSize = () => _cache.size;

// Exports for tests
export { cacheGet as __cacheGetForTests, cachePut as __cachePutForTests, CACHE_MAX };
